import React from "react";
import { format2 } from "../../helpers/helper";
import { Box, Typography, Skeleton } from "@mui/material";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useSalesVsBudget } from "../../hooks/useSalesVsBudget";

function ProjectSalesCard({ dateParams, isDateRangeIncomplete }) {
    const {
        data,
        achievedPercent,
        deltaPercent,
        isLoading,
        error,
    } = useSalesVsBudget({
        categoryId: "PRJ",
        dateParams,
        isDateRangeIncomplete,
    });
    const isPositive = deltaPercent >= 100;
    const DeltaIcon = isPositive ? TrendingUpIcon : TrendingDownIcon;
    const deltaColor = isPositive ? "#22C55E" : "#EF4444";

    if (isLoading) {
        return (
            <Box sx={{ minHeight: 110, p: 2, borderRadius: "20px", bgcolor: "background.paper" }}>
                <Skeleton width="80%" height={16} />
                <Skeleton width="40%" height={28} />
                <Skeleton width="60%" height={14} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ minHeight: 110, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.paper" }}>
                <Typography sx={{ color: "error.main", fontSize: 12 }}>
                    {error}
                </Typography>
            </Box>
        );
    }

    if (isDateRangeIncomplete) {
        return (
            <Box
                sx={{
                    minHeight: 110,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                    borderRadius: "20px",
                    bgcolor: "background.paper",
                }}
            >
                <Typography sx={{ color: "warning.main", fontSize: 12 }}>
                    Select both From & To dates
                </Typography>
            </Box>
        );
    }

    if (data.achieved === 0 && data.budget === 0) {
        return (
            <Box sx={{ minHeight: 110, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.paper" }}>
                <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                    No project sales data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: 110,
                p: { xs: 2, lg: 1.5, xl: 2 },
                borderRadius: "20px",
                bgcolor: "background.paper",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "all 0.2s ease",
                "&:hover": { transform: "translateY(-2px)" },
                boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
            }}
        >
            <Typography 
                sx={{ 
                    fontSize: { xs: 12, lg: 11, xl: 13 }, 
                    color: "secondary.main",
                    lineHeight: 1.2,
                    mb: 1
                }}
            >
                Project Sales Achieved Against Budget
            </Typography>

            <Box 
                sx={{ 
                    display: "flex", 
                    alignItems: "flex-end", 
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 0.5
                }}
            >
                <Typography 
                    sx={{ 
                        fontSize: { xs: 22, lg: 16, xl: 18 }, 
                        fontWeight: 700, 
                        color: "text.primary",
                    }}
                >
                    {format2(achievedPercent)} %
                </Typography>

                <Box 
                    sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: "2px", 
                        fontSize: { xs: 11, lg: 9, xl: 11 }, 
                        color: deltaColor,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {format2(Math.abs(deltaPercent))} %
                        <DeltaIcon sx={{ fontSize: "inherit", ml: "2px" }} />
                    </Box>
                    <Typography 
                        component="span" 
                        sx={{ 
                            fontSize: "inherit", 
                            color: deltaColor,
                            ml: "2px"
                        }}
                    >
                        vs pro rata
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

export default React.memo(ProjectSalesCard);